import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getPortefeuilles, getPortefeuilleNonAffectes, getPortefeuilleDashboard,
    transferPortefeuille, bulkAssignPortefeuille,
    getPortefeuilleRegles, createPortefeuilleRegle, updatePortefeuilleRegle,
    deletePortefeuilleRegle, applyPortefeuilleRegle,
    getCommerciaux, getZones, getSegments
} from '../services/api';
import {
    FiBriefcase, FiUsers, FiDollarSign, FiAlertTriangle, FiTrendingUp,
    FiArrowRight, FiSettings, FiPlus, FiEye, FiSearch, FiPlay, FiTrash2, FiEdit2, FiX, FiCheck
} from 'react-icons/fi';
import SearchableSelect from '../components/SearchableSelect';

type Tab = 'overview' | 'unassigned' | 'rules';

const STATUT_COLORS: Record<string, string> = {
    a_jour: '#16A34A',
    retard_leger: '#F59E0B',
    retard_serieux: '#EF4444',
    contentieux: '#7C2D12'
};
const STATUT_LABELS: Record<string, string> = {
    a_jour: 'À jour',
    retard_leger: 'Retard léger',
    retard_serieux: 'Retard sérieux',
    contentieux: 'Contentieux'
};

const fmt = (n: any) => {
    const v = Number(n) || 0;
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
};

const Portefeuilles: React.FC = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>('overview');
    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState<any>(null);
    const [commerciaux, setCommerciaux] = useState<any[]>([]);
    const [unassigned, setUnassigned] = useState<any[]>([]);
    const [regles, setRegles] = useState<any[]>([]);
    const [allCommerciaux, setAllCommerciaux] = useState<any[]>([]);
    const [zones, setZones] = useState<any[]>([]);
    const [segments, setSegments] = useState<any[]>([]);

    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('');

    const [transferOpen, setTransferOpen] = useState(false);
    const [transferIds, setTransferIds] = useState<number[]>([]);
    const [transferTarget, setTransferTarget] = useState('');
    const [transferMotif, setTransferMotif] = useState('');

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFilters, setBulkFilters] = useState<any>({ zone_id: '', segment_id: '', potentiel: '', non_affectes: true });
    const [bulkTarget, setBulkTarget] = useState('');
    const [bulkMotif, setBulkMotif] = useState('');

    const [ruleOpen, setRuleOpen] = useState(false);
    const [editRule, setEditRule] = useState<any>(null);
    const [ruleForm, setRuleForm] = useState<any>({ nom: '', description: '', type_regle: 'zone', cible_commercial_id: '', cible_zone_id: '', charge_max: '', priorite: 100, actif: 1, conditions: { potentiel: '' } });

    const reload = () => {
        setLoading(true);
        Promise.all([
            getPortefeuilles({ search, statut: filterStatut }),
            getPortefeuilleDashboard(),
            getPortefeuilleNonAffectes(),
            getPortefeuilleRegles(),
        ])
        .then(([pRes, dRes, naRes, rRes]) => {
            setCommerciaux(pRes.data.data);
            setDashboard({ ...dRes.data, totals: pRes.data.totals });
            setUnassigned(naRes.data.data);
            setRegles(rRes.data.data);
        })
        .finally(() => setLoading(false));
    };

    useEffect(() => {
        Promise.all([getCommerciaux(), getZones(), getSegments()]).then(([cRes, zRes, sRes]) => {
            setAllCommerciaux(cRes.data || []);
            setZones(zRes.data || []);
            setSegments(sRes.data || []);
        });
    }, []);

    useEffect(() => { reload(); }, [search, filterStatut]);

    const openTransferOne = (clientId: number) => {
        setTransferIds([clientId]); setTransferTarget(''); setTransferMotif(''); setTransferOpen(true);
    };
    const openTransferMany = () => {
        if (transferIds.length === 0) return;
        setTransferTarget(''); setTransferMotif(''); setTransferOpen(true);
    };

    const handleTransfer = async () => {
        try {
            await transferPortefeuille({
                client_ids: transferIds,
                nouveau_commercial_id: transferTarget ? parseInt(transferTarget) : null,
                motif: transferMotif
            });
            setTransferOpen(false); setTransferIds([]); reload();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const handleBulk = async () => {
        if (!bulkTarget) return alert('Sélectionnez un commercial cible');
        try {
            const filters: any = {};
            if (bulkFilters.zone_id) filters.zone_id = bulkFilters.zone_id;
            if (bulkFilters.segment_id) filters.segment_id = bulkFilters.segment_id;
            if (bulkFilters.potentiel) filters.potentiel = bulkFilters.potentiel;
            if (bulkFilters.non_affectes) filters.non_affectes = true;
            const res = await bulkAssignPortefeuille({
                filters, commercial_id: parseInt(bulkTarget), motif: bulkMotif
            });
            alert(res.data.message);
            setBulkOpen(false); reload();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const openRule = (r?: any) => {
        if (r) {
            setEditRule(r);
            let conds = {};
            try { conds = typeof r.conditions === 'string' ? JSON.parse(r.conditions) : (r.conditions || {}); } catch {}
            setRuleForm({
                nom: r.nom, description: r.description || '', type_regle: r.type_regle,
                cible_commercial_id: r.cible_commercial_id || '', cible_zone_id: r.cible_zone_id || '',
                charge_max: r.charge_max || '', priorite: r.priorite, actif: r.actif,
                conditions: conds
            });
        } else {
            setEditRule(null);
            setRuleForm({ nom: '', description: '', type_regle: 'zone', cible_commercial_id: '', cible_zone_id: '', charge_max: '', priorite: 100, actif: 1, conditions: { potentiel: '' } });
        }
        setRuleOpen(true);
    };

    const saveRule = async () => {
        if (!ruleForm.nom || !ruleForm.cible_commercial_id) return alert('Nom et commercial cible requis');
        try {
            const payload = {
                ...ruleForm,
                cible_commercial_id: parseInt(ruleForm.cible_commercial_id),
                cible_zone_id: ruleForm.cible_zone_id ? parseInt(ruleForm.cible_zone_id) : null,
                charge_max: ruleForm.charge_max ? parseInt(ruleForm.charge_max) : null,
                conditions: ruleForm.conditions
            };
            if (editRule) await updatePortefeuilleRegle(editRule.id, payload);
            else await createPortefeuilleRegle(payload);
            setRuleOpen(false); reload();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const applyRule = async (id: number) => {
        if (!window.confirm('Appliquer cette règle aux clients non affectés correspondants ?')) return;
        try {
            const res = await applyPortefeuilleRegle(id);
            alert(res.data.message);
            reload();
        } catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const removeRule = async (id: number) => {
        if (!window.confirm('Supprimer cette règle ?')) return;
        try { await deletePortefeuilleRegle(id); reload(); }
        catch (e: any) { alert(e.response?.data?.error || 'Erreur'); }
    };

    const stats = dashboard?.global || {};

    const commerciauxOptions = useMemo(() =>
        allCommerciaux.map((c: any) => ({ value: String(c.id), label: `${c.prenom} ${c.nom}`, sub: c.zone_nom || c.role || '' })),
        [allCommerciaux]
    );
    const zonesOptions = useMemo(() =>
        zones.map((z: any) => ({ value: String(z.id), label: z.nom, sub: z.description || '' })),
        [zones]
    );
    const segmentsOptions = useMemo(() =>
        segments.map((s: any) => ({ value: String(s.id), label: s.nom, sub: s.description || '' })),
        [segments]
    );

    return (
        <div className="page-container portefeuilles-page">
            <div className="page-header">
                <div>
                    <h1><FiBriefcase /> Portefeuilles Clients</h1>
                    <p className="page-subtitle">Vision financière, affectation et règles d'attribution</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
                        <FiUsers /> Affectation en masse
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="pf-kpi-grid">
                <div className="pf-kpi pf-kpi-blue">
                    <div className="pf-kpi-icon"><FiUsers /></div>
                    <div className="pf-kpi-content">
                        <div className="pf-kpi-label">Clients gérés</div>
                        <div className="pf-kpi-value">{stats.nb_clients || 0}</div>
                        <div className="pf-kpi-sub">{stats.nb_non_affectes || 0} non affectés</div>
                    </div>
                </div>
                <div className="pf-kpi pf-kpi-green">
                    <div className="pf-kpi-icon"><FiDollarSign /></div>
                    <div className="pf-kpi-content">
                        <div className="pf-kpi-label">Encours total</div>
                        <div className="pf-kpi-value">{fmt(stats.total_encours)} <span className="pf-kpi-unit">FCFA</span></div>
                        <div className="pf-kpi-sub">Plafond: {fmt(stats.total_plafond)}</div>
                    </div>
                </div>
                <div className="pf-kpi pf-kpi-purple">
                    <div className="pf-kpi-icon"><FiTrendingUp /></div>
                    <div className="pf-kpi-content">
                        <div className="pf-kpi-label">CA annuel cumulé</div>
                        <div className="pf-kpi-value">{fmt(stats.total_ca_annuel)} <span className="pf-kpi-unit">FCFA</span></div>
                        <div className="pf-kpi-sub">Score moyen: {stats.score_moyen || 0}/100</div>
                    </div>
                </div>
                <div className="pf-kpi pf-kpi-red">
                    <div className="pf-kpi-icon"><FiAlertTriangle /></div>
                    <div className="pf-kpi-content">
                        <div className="pf-kpi-label">Créances</div>
                        <div className="pf-kpi-value">{fmt(stats.total_creance)} <span className="pf-kpi-unit">FCFA</span></div>
                        <div className="pf-kpi-sub">{(Number(stats.nb_retard_serieux) || 0) + (Number(stats.nb_contentieux) || 0)} clients à risque</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="pf-tabs">
                <button className={`pf-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
                    Vue par commercial ({commerciaux.length})
                </button>
                <button className={`pf-tab ${tab === 'unassigned' ? 'active' : ''}`} onClick={() => setTab('unassigned')}>
                    Clients non affectés ({unassigned.length})
                </button>
                <button className={`pf-tab ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
                    Règles d'attribution ({regles.length})
                </button>
            </div>

            {tab === 'overview' && (
                <div className="pf-section">
                    <div className="pf-filters">
                        <div className="pf-search">
                            <FiSearch />
                            <input type="text" placeholder="Rechercher un commercial..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                            <option value="">Tous statuts</option>
                            {Object.entries(STATUT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>

                    {loading ? <div className="pf-loading">Chargement...</div> : (
                        <div className="pf-table-wrap">
                            <table className="pf-table">
                                <thead>
                                    <tr>
                                        <th>Commercial</th>
                                        <th>Zone</th>
                                        <th className="num">Clients</th>
                                        <th className="num">Encours</th>
                                        <th className="num">CA annuel</th>
                                        <th className="num">Créances</th>
                                        <th className="num">Score</th>
                                        <th className="num">À risque</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {commerciaux.length === 0 && (
                                        <tr><td colSpan={9} className="pf-empty">Aucun commercial trouvé</td></tr>
                                    )}
                                    {commerciaux.map(c => (
                                        <tr key={c.id}>
                                            <td>
                                                <div className="pf-user">
                                                    <div className="pf-avatar">{c.prenom?.[0]}{c.nom?.[0]}</div>
                                                    <div>
                                                        <div className="pf-user-name">{c.prenom} {c.nom}</div>
                                                        <div className="pf-user-role">{c.role}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{c.zone_nom || <span className="muted">—</span>}</td>
                                            <td className="num"><strong>{c.nb_clients}</strong></td>
                                            <td className="num">{fmt(c.total_encours)}</td>
                                            <td className="num">{fmt(c.total_ca_annuel)}</td>
                                            <td className="num">
                                                <span style={{ color: Number(c.total_creance) > 0 ? '#EF4444' : '#94A3B8' }}>
                                                    {fmt(c.total_creance)}
                                                </span>
                                            </td>
                                            <td className="num">
                                                <span className="pf-score" style={{ background: `hsl(${(c.score_moyen || 0) * 1.2}, 70%, 92%)`, color: `hsl(${(c.score_moyen || 0) * 1.2}, 70%, 30%)` }}>
                                                    {c.score_moyen || 0}
                                                </span>
                                            </td>
                                            <td className="num">
                                                {c.nb_clients_risque > 0 ? (
                                                    <span className="pf-badge-risk">{c.nb_clients_risque}</span>
                                                ) : <span className="muted">0</span>}
                                            </td>
                                            <td>
                                                <button className="pf-icon-btn" title="Voir le détail" onClick={() => navigate(`/admin/commerciaux/${c.id}`)}>
                                                    <FiEye />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === 'unassigned' && (
                <div className="pf-section">
                    <div className="pf-section-header">
                        <h3>Clients non affectés à un commercial</h3>
                        {transferIds.length > 0 && (
                            <button className="btn btn-primary" onClick={openTransferMany}>
                                <FiArrowRight /> Affecter ({transferIds.length})
                            </button>
                        )}
                    </div>

                    {unassigned.length === 0 ? (
                        <div className="pf-empty-state">
                            <FiCheck size={32} />
                            <p>Tous les clients sont affectés.</p>
                        </div>
                    ) : (
                        <div className="pf-table-wrap">
                            <table className="pf-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 32 }}>
                                            <input type="checkbox"
                                                checked={transferIds.length === unassigned.length && unassigned.length > 0}
                                                onChange={e => setTransferIds(e.target.checked ? unassigned.map(c => c.id) : [])}
                                            />
                                        </th>
                                        <th>Client</th>
                                        <th>Ville</th>
                                        <th>Zone</th>
                                        <th>Potentiel</th>
                                        <th className="num">Encours</th>
                                        <th className="num">CA annuel</th>
                                        <th>Statut</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unassigned.map(c => (
                                        <tr key={c.id}>
                                            <td>
                                                <input type="checkbox"
                                                    checked={transferIds.includes(c.id)}
                                                    onChange={e => setTransferIds(e.target.checked
                                                        ? [...transferIds, c.id]
                                                        : transferIds.filter(id => id !== c.id))}
                                                />
                                            </td>
                                            <td><strong>{c.raison_sociale}</strong></td>
                                            <td>{c.ville || <span className="muted">—</span>}</td>
                                            <td>{c.zone_nom || <span className="muted">—</span>}</td>
                                            <td>{c.potentiel}</td>
                                            <td className="num">{fmt(c.encours_actuel)}</td>
                                            <td className="num">{fmt(c.ca_annuel)}</td>
                                            <td>
                                                <span className="pf-statut-pill" style={{ background: STATUT_COLORS[c.statut_paiement] + '22', color: STATUT_COLORS[c.statut_paiement] }}>
                                                    {STATUT_LABELS[c.statut_paiement]}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="pf-icon-btn" title="Affecter" onClick={() => openTransferOne(c.id)}>
                                                    <FiArrowRight />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === 'rules' && (
                <div className="pf-section">
                    <div className="pf-section-header">
                        <h3>Règles d'attribution automatique</h3>
                        <button className="btn btn-primary" onClick={() => openRule()}>
                            <FiPlus /> Nouvelle règle
                        </button>
                    </div>

                    {regles.length === 0 ? (
                        <div className="pf-empty-state">
                            <FiSettings size={32} />
                            <p>Aucune règle définie.</p>
                            <p className="muted">Créez une règle pour automatiser l'affectation des nouveaux clients.</p>
                        </div>
                    ) : (
                        <div className="pf-rules-grid">
                            {regles.map(r => (
                                <div key={r.id} className={`pf-rule-card ${r.actif ? '' : 'inactive'}`}>
                                    <div className="pf-rule-header">
                                        <div>
                                            <div className="pf-rule-name">{r.nom}</div>
                                            <div className="pf-rule-type">{r.type_regle} · priorité {r.priorite}</div>
                                        </div>
                                        <span className={`pf-rule-status ${r.actif ? 'active' : ''}`}>
                                            {r.actif ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    {r.description && <p className="pf-rule-desc">{r.description}</p>}
                                    <div className="pf-rule-meta">
                                        {r.commercial_prenom && (
                                            <span><strong>Cible:</strong> {r.commercial_prenom} {r.commercial_nom}</span>
                                        )}
                                        {r.zone_nom && <span><strong>Zone:</strong> {r.zone_nom}</span>}
                                        {r.charge_max && <span><strong>Charge max:</strong> {r.charge_max}</span>}
                                    </div>
                                    <div className="pf-rule-stats">
                                        <span>{r.nb_clients_affectes} clients affectés</span>
                                        {r.derniere_application && (
                                            <span className="muted">Dernière exéc: {new Date(r.derniere_application).toLocaleDateString('fr-FR')}</span>
                                        )}
                                    </div>
                                    <div className="pf-rule-actions">
                                        <button className="btn-sm btn-primary" onClick={() => applyRule(r.id)} disabled={!r.actif}>
                                            <FiPlay /> Appliquer
                                        </button>
                                        <button className="btn-sm" onClick={() => openRule(r)}><FiEdit2 /></button>
                                        <button className="btn-sm btn-danger" onClick={() => removeRule(r.id)}><FiTrash2 /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Transfer Modal */}
            {transferOpen && (
                <div className="pf-modal-backdrop" onClick={() => setTransferOpen(false)}>
                    <div className="pf-modal" onClick={e => e.stopPropagation()}>
                        <div className="pf-modal-header">
                            <h3>Transférer {transferIds.length} client{transferIds.length > 1 ? 's' : ''}</h3>
                            <button onClick={() => setTransferOpen(false)}><FiX /></button>
                        </div>
                        <div className="pf-modal-body">
                            <label>Nouveau commercial</label>
                            <SearchableSelect
                                value={transferTarget}
                                onChange={(v: string) => setTransferTarget(v)}
                                placeholder="— Désaffecter (sans commercial) —"
                                options={commerciauxOptions}
                                icon={<FiUsers />}
                            />
                            <label>Motif</label>
                            <textarea rows={3} value={transferMotif} onChange={e => setTransferMotif(e.target.value)}
                                placeholder="Optionnel — ex: réorganisation, départ, déséquilibre de charge..." />
                        </div>
                        <div className="pf-modal-footer">
                            <button className="btn" onClick={() => setTransferOpen(false)}>Annuler</button>
                            <button className="btn btn-primary" onClick={handleTransfer}>Confirmer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Modal */}
            {bulkOpen && (
                <div className="pf-modal-backdrop" onClick={() => setBulkOpen(false)}>
                    <div className="pf-modal" onClick={e => e.stopPropagation()}>
                        <div className="pf-modal-header">
                            <h3>Affectation en masse</h3>
                            <button onClick={() => setBulkOpen(false)}><FiX /></button>
                        </div>
                        <div className="pf-modal-body">
                            <label>Filtrer les clients</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                                <SearchableSelect
                                    value={bulkFilters.zone_id}
                                    onChange={(v: string) => setBulkFilters({ ...bulkFilters, zone_id: v })}
                                    placeholder="Toutes zones"
                                    options={zonesOptions}
                                />
                                <SearchableSelect
                                    value={bulkFilters.segment_id}
                                    onChange={(v: string) => setBulkFilters({ ...bulkFilters, segment_id: v })}
                                    placeholder="Tous segments"
                                    options={segmentsOptions}
                                />
                            </div>
                            <select value={bulkFilters.potentiel} onChange={e => setBulkFilters({ ...bulkFilters, potentiel: e.target.value })}>
                                <option value="">Tout potentiel</option>
                                <option value="faible">Faible</option>
                                <option value="moyen">Moyen</option>
                                <option value="eleve">Élevé</option>
                                <option value="tres_eleve">Très élevé</option>
                            </select>
                            <label className="pf-checkbox">
                                <input type="checkbox" checked={bulkFilters.non_affectes}
                                    onChange={e => setBulkFilters({ ...bulkFilters, non_affectes: e.target.checked })} />
                                Uniquement les clients non affectés
                            </label>
                            <label>Affecter à</label>
                            <SearchableSelect
                                value={bulkTarget}
                                onChange={(v: string) => setBulkTarget(v)}
                                placeholder="— Choisir un commercial —"
                                options={commerciauxOptions}
                                icon={<FiUsers />}
                            />
                            <label>Motif</label>
                            <input type="text" value={bulkMotif} onChange={e => setBulkMotif(e.target.value)}
                                placeholder="Ex: Affectation initiale zone Dakar" />
                        </div>
                        <div className="pf-modal-footer">
                            <button className="btn" onClick={() => setBulkOpen(false)}>Annuler</button>
                            <button className="btn btn-primary" onClick={handleBulk}>Lancer l'affectation</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rule Modal */}
            {ruleOpen && (
                <div className="pf-modal-backdrop" onClick={() => setRuleOpen(false)}>
                    <div className="pf-modal" onClick={e => e.stopPropagation()}>
                        <div className="pf-modal-header">
                            <h3>{editRule ? 'Modifier la règle' : 'Nouvelle règle'}</h3>
                            <button onClick={() => setRuleOpen(false)}><FiX /></button>
                        </div>
                        <div className="pf-modal-body">
                            <label>Nom *</label>
                            <input type="text" value={ruleForm.nom} onChange={e => setRuleForm({ ...ruleForm, nom: e.target.value })}
                                placeholder="Ex: Nouveaux clients zone Dakar" />
                            <label>Description</label>
                            <textarea rows={2} value={ruleForm.description} onChange={e => setRuleForm({ ...ruleForm, description: e.target.value })} />

                            <div className="pf-form-row">
                                <div>
                                    <label>Type</label>
                                    <select value={ruleForm.type_regle} onChange={e => setRuleForm({ ...ruleForm, type_regle: e.target.value })}>
                                        <option value="zone">Par zone</option>
                                        <option value="segment">Par segment</option>
                                        <option value="potentiel">Par potentiel</option>
                                        <option value="charge_max">Charge max</option>
                                        <option value="mixte">Mixte</option>
                                    </select>
                                </div>
                                <div>
                                    <label>Priorité</label>
                                    <input type="number" value={ruleForm.priorite}
                                        onChange={e => setRuleForm({ ...ruleForm, priorite: parseInt(e.target.value) || 100 })} />
                                </div>
                            </div>

                            <label>Zone (cible)</label>
                            <SearchableSelect
                                value={ruleForm.cible_zone_id ? String(ruleForm.cible_zone_id) : ''}
                                onChange={(v: string) => setRuleForm({ ...ruleForm, cible_zone_id: v })}
                                placeholder="Toutes zones"
                                options={zonesOptions}
                            />

                            <label>Potentiel min (condition)</label>
                            <select value={ruleForm.conditions?.potentiel || ''}
                                onChange={e => setRuleForm({ ...ruleForm, conditions: { ...ruleForm.conditions, potentiel: e.target.value } })}>
                                <option value="">Aucun</option>
                                <option value="faible">Faible</option>
                                <option value="moyen">Moyen</option>
                                <option value="eleve">Élevé</option>
                                <option value="tres_eleve">Très élevé</option>
                            </select>

                            <label>Commercial cible *</label>
                            <SearchableSelect
                                value={ruleForm.cible_commercial_id ? String(ruleForm.cible_commercial_id) : ''}
                                onChange={(v: string) => setRuleForm({ ...ruleForm, cible_commercial_id: v })}
                                placeholder="— Choisir un commercial —"
                                options={commerciauxOptions}
                                icon={<FiUsers />}
                            />

                            <label>Charge max (clients)</label>
                            <input type="number" value={ruleForm.charge_max}
                                onChange={e => setRuleForm({ ...ruleForm, charge_max: e.target.value })}
                                placeholder="Optionnel — limite le nombre total de clients" />

                            <label className="pf-checkbox">
                                <input type="checkbox" checked={!!ruleForm.actif}
                                    onChange={e => setRuleForm({ ...ruleForm, actif: e.target.checked ? 1 : 0 })} />
                                Règle active
                            </label>
                        </div>
                        <div className="pf-modal-footer">
                            <button className="btn" onClick={() => setRuleOpen(false)}>Annuler</button>
                            <button className="btn btn-primary" onClick={saveRule}>Enregistrer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Portefeuilles;
